// src/entities/npc.ts — NPCs de Villa Aurelia + Ruta (Fase 2, originales)
import type { ZoneId } from '../data/gameConfig.js';

export type NPCKind = 'habitante'|'comerciante'|'aventurero'|'nino'|'investigador'|'misterioso';

export type NPCDef = {
  id: string;
  name: string;
  kind: NPCKind;
  zone: ZoneId;
  x: number; y: number; // tile
  facing: 'north'|'south'|'east'|'west';
  color: string; // sprite base
  accent: string;
  dialogues: string[][]; // cada entrada es una página de líneas; rotan o según flag
  hint?: string; // pista corta para HUD
};

export const NPCS: NPCDef[] = [
  // — VILLA AURELIA —
  {
    id: 'alba', name: 'Alba', kind: 'habitante', zone: 'villa_aurelia', x: 12, y: 13, facing: 'south', color: '#ffb86b', accent: '#6a5cff',
    dialogues: [
      ["¡Bienvenido a Villa Aurelia!", "Soy Alba, cuido la plaza. Si te pierdes, sigue el camino dorado."],
      ["¿Viste la Fuente de la Aurora?", "Dicen que el primer explorador dejó allí un cristal que aún brilla por las noches."],
      ["Consejo: habla con todos. En Aetheria, hasta un niño sabe un secreto."]
    ], hint: "Habla — Alba"
  },
  {
    id: 'nino_tobi', name: 'Tobi', kind: 'nino', zone: 'villa_aurelia', x: 18, y: 12, facing: 'west', color: '#7cf0ff', accent: '#ffb86b',
    dialogues: [
      ["¡Corre que te alcanzo! ¡Ay, me choqué con un árbol!", "¿Quieres jugar a buscar el claro escondido? Está al este de la Ruta."],
      ["Mi hermana dice que las Asterias viven en la hierba alta.", "¡Yo una vez vi una luz azul entre la hierba!"],
    ]
  },
  {
    id: 'anciano_bren', name: 'Bren', kind: 'habitante', zone: 'villa_aurelia', x: 10, y: 11, facing: 'east', color: '#c9b896', accent: '#4a5560',
    dialogues: [
      ["Villa Aurelia era solo tres casas cuando yo era joven.", "Ahora los caminos llegan hasta la Ruta. El mundo crece, como tú."],
      ["Si vas a la Ruta, lleva pan. Y no pises el agua. El Río Zarco no perdona distraídos."],
    ]
  },
  {
    id: 'investigador_kai', name: 'Kai', kind: 'investigador', zone: 'villa_aurelia', x: 15, y: 17, facing: 'north', color: '#6a5cff', accent: '#7cf0ff',
    dialogues: [
      ["Soy Kai, cartógrafo de Aetheria.", "Estudio esa ruina de la Ruta. Las piedras no son de aquí... parecen caídas del cielo."],
      ["¿Piedra misteriosa?", "Cada noche, a las 20:00, vibra. Si estás allí a esa hora, quizá veas algo."],
      ["Toma nota: la zona sur de la Ruta está bloqueada. Necesitamos despejar el derrumbe. Fase 3... digo, pronto."]
    ]
  },
  {
    id: 'misteriosa_lya', name: 'Lya', kind: 'misterioso', zone: 'villa_aurelia', x: 22, y: 14, facing: 'south', color: '#2a2f4a', accent: '#ff6b9d',
    dialogues: [
      ["...", "No todos los caminos llevan a donde crees.", "Guarda tu posición antes de cruzar el puente."],
      ["¿Buscas Asterias?", "Aún no es el momento. Pero escucha la hierba alta. Algún día responderá."]
    ]
  },
  // Interiores Villa
  {
    id: 'madre', name: 'Mamá', kind: 'habitante', zone: 'casa_jugador', x: 4, y: 5, facing: 'east', color: '#e8a0bf', accent: '#6a5cff',
    dialogues: [
      ["¡Mi explorador ya despierto!", "Toma, lleva pan. Y no olvides despedirte de Alba en la plaza."],
      ["Estoy orgullosa. Villa Aurelia siempre será tu hogar, aunque el mundo te llame."]
    ]
  },
  {
    id: 'liora', name: 'Liora', kind: 'comerciante', zone: 'tienda_aurelia', x: 6, y: 4, facing: 'south', color: '#ffb86b', accent: '#1e6fa8',
    dialogues: [
      ["¡Bienvenido al Bazar de Liora!", "Por ahora solo tengo charla y ánimos. Pronto traeré pociones y cristales."],
      ["Consejo de comerciante: guarda bien tus cristales lumen. Iluminan la noche y atraen Asterias curiosas."],
    ]
  },
  {
    id: 'solea', name: 'Solea', kind: 'habitante', zone: 'centro_aurelia', x: 6, y: 4, facing: 'south', color: '#b7ffdb', accent: '#6a5cff',
    dialogues: [
      ["Bienvenido al Centro de Descanso.", "Aquí tus futuras Asterias descansarán y se recuperarán. Por ahora, tú descansa."],
      ["¿Sabías que el centro nunca cierra? Incluso de noche, una luz queda encendida. Por si alguien vuelve tarde de la Ruta."]
    ]
  },
  {
    id: 'profesor_ori', name: 'Prof. Ori', kind: 'investigador', zone: 'casa_npc', x: 6, y: 4, facing: 'south', color: '#7cf0ff', accent: '#ffcf6b',
    dialogues: [
      ["¡Oh! Un visitante. Soy Ori, estudio las Asterias de tipo Astra.", "Esas que brillan solo de noche... como la que a veces se ve tras la cascada."],
      ["Si ves huellas brillantes en la hierba alta de la Ruta, avísame. Pago con historias."],
    ]
  },
  // RUTA AURELIA
  {
    id: 'aventurera_mira', name: 'Mira', kind: 'aventurero', zone: 'ruta_aurelia', x: 16, y: 8, facing: 'west', color: '#ff6b6b', accent: '#ffcf6b',
    dialogues: [
      ["¡No cruces el puente distraído!", "El Río Zarco suena fuerte, pero el puente es seguro. Yo lo crucé cien veces."],
      ["¿Vas al claro escondido? Sigue el sendero pálido al este. Hay flores que solo abren al atardecer."]
    ]
  },
  {
    id: 'nina_ruta', name: 'Nina', kind: 'nino', zone: 'ruta_aurelia', x: 12, y: 21, facing: 'north', color: '#ffcf6b', accent: '#6a5cff',
    dialogues: [
      ["¡Dejé una cinta en el Árbol Madre!", "¿La viste? Es amarilla. Dicen que trae suerte a los que inician su viaje."],
    ]
  },
  {
    id: 'pescador', name: 'Joren', kind: 'habitante', zone: 'ruta_aurelia', x: 25, y: 12, facing: 'west', color: '#1e6fa8', accent: '#b7ffdb',
    dialogues: [
      ["Shhh... si te quedas quieto junto al agua, a veces ves sombras.", "No son peces. Son otra cosa. Ya verás."],
    ]
  },
];

export function npcsInZone(zone: ZoneId): NPCDef[] {
  return NPCS.filter(n => n.zone === zone);
}

export function getNpc(id: string): NPCDef | undefined {
  return NPCS.find(n => n.id === id);
}
